/**
 * condoDocumentsFormat.js — date handling for the condo documents panel.
 *
 * Two kinds of date come from the server:
 * - timestamps (`delivered_at`, `buyer_received_at`, …): naive UTC ISO strings
 *   (no `Z`), shown in the property's zone;
 * - local calendar dates (`review_deadline_local_date`, `closing_date`):
 *   `YYYY-MM-DD` already in the property's zone. They are formatted as-is and
 *   never pass through the browser's zone, which would shift them a day.
 */

/** Wisconsin is the only state with a condo documents rule today. */
export const DEFAULT_PROPERTY_TIMEZONE = 'America/Chicago';

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const validZoneCache = new Map();

/**
 * An IANA zone Intl accepts, or the property default. The server sends
 * `deadline.time_zone` (e.g. "America/Chicago"); an older server sent a label
 * ("Central time") that Intl rejects with a RangeError, which would unmount
 * the page. Never throws.
 */
export function resolveTimeZone(zone) {
  if (!zone || typeof zone !== 'string') return DEFAULT_PROPERTY_TIMEZONE;
  if (!validZoneCache.has(zone)) {
    let ok = false;
    try {
      new Intl.DateTimeFormat('en-US', { timeZone: zone }).format(new Date(0));
      ok = true;
    } catch (err) {
      ok = false;
    }
    validZoneCache.set(zone, ok);
  }
  return validZoneCache.get(zone) ? zone : DEFAULT_PROPERTY_TIMEZONE;
}

/** Parse a server timestamp; a naive ISO string is UTC. */
export function parseServerTimestamp(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  const text = String(value);
  const hasZone = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(text);
  const date = new Date(hasZone ? text : `${text}Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** `YYYY-MM-DD` (or a longer ISO string) → a Date at UTC midnight, or null. */
export function parseLocalDate(value) {
  if (!value) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (!match) return null;
  const [, y, m, d] = match.map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

const LONG_DATE_OPTIONS = { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' };

/**
 * "Friday, October 9, 2026" for a local calendar date: the format the legal
 * copy uses for every `{date}`-style placeholder.
 */
export function formatLongLocalDate(value) {
  const date = parseLocalDate(value);
  if (!date) return '';
  return date.toLocaleDateString('en-US', { ...LONG_DATE_OPTIONS, timeZone: 'UTC' });
}

/** "Friday, October 9, 2026" for a server timestamp, in the property's zone. */
export function formatLongTimestamp(value, timeZone = DEFAULT_PROPERTY_TIMEZONE) {
  const date = parseServerTimestamp(value);
  if (!date) return '';
  return date.toLocaleDateString('en-US', { ...LONG_DATE_OPTIONS, timeZone: resolveTimeZone(timeZone) });
}

/** "Oct 9, 2026" for a local calendar date. */
export function formatLocalDate(value) {
  const date = parseLocalDate(value);
  if (!date) return '';
  return date.toLocaleDateString('en-US', {
    timeZone: 'UTC',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** "Oct 9, 2026" for a server timestamp, in the property's zone. */
export function formatTimestamp(value, timeZone = DEFAULT_PROPERTY_TIMEZONE) {
  const date = parseServerTimestamp(value);
  if (!date) return '';
  return date.toLocaleDateString('en-US', {
    timeZone: resolveTimeZone(timeZone),
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Today's `YYYY-MM-DD` in the property's zone. */
export function todayLocalYmd(timeZone = DEFAULT_PROPERTY_TIMEZONE, now = new Date()) {
  // en-CA formats as YYYY-MM-DD.
  return now.toLocaleDateString('en-CA', { timeZone: resolveTimeZone(timeZone) });
}

/** A server timestamp's `YYYY-MM-DD` in the property's zone. */
export function timestampToLocalYmd(value, timeZone = DEFAULT_PROPERTY_TIMEZONE) {
  const date = parseServerTimestamp(value);
  return date ? todayLocalYmd(timeZone, date) : null;
}

/** Add calendar days to a `YYYY-MM-DD`; returns `YYYY-MM-DD`. */
export function addCalendarDays(ymd, days) {
  const date = parseLocalDate(ymd);
  if (!date) return null;
  return new Date(date.getTime() + days * MS_PER_DAY).toISOString().slice(0, 10);
}

/**
 * The same `YYYY-MM-DD`, or the Friday before it when it falls on a weekend.
 *
 * Legal §5.4: a deliver-by date on a weekend or legal holiday is shown as the
 * preceding business day. The client knows weekends only; the server's
 * `deliver_by_local_date` (holiday-aware) wins whenever it is present.
 */
export function weekdayOnOrBefore(ymd) {
  const date = parseLocalDate(ymd);
  if (!date) return null;
  const day = date.getUTCDay(); // 0 Sunday … 6 Saturday
  if (day === 6) return addCalendarDays(ymd, -1);
  if (day === 0) return addCalendarDays(ymd, -2);
  return String(ymd).slice(0, 10);
}

/** Compare two `YYYY-MM-DD` strings: negative, zero or positive. */
export function compareYmd(a, b) {
  return String(a).slice(0, 10).localeCompare(String(b).slice(0, 10));
}
